export interface QuestionField {
  id: string;
  type: string;
  label: string;
  options?: unknown[];
  deprecated?: boolean;
  [key: string]: unknown;
}

export interface QuestionMapping {
  id: string;
  sourceId: string;
  targetId: string;
  sharedKey: string;
  sourceLabel: string;
  targetLabel: string;
  sourceType: string;
  targetType: string;
}

export interface ReviewItem {
  id: number;
  name: string;
  description?: string;
  questionsSchema?: {
    fields: QuestionField[];
  };
  sharedWith?: unknown[];
}

export interface ReviewCategory {
  id: number;
  name: string;
  items?: ReviewItem[];
  subcategories?: ReviewCategory[];
}

export interface ReviewTemplate {
  id: number;
  name: string;
  type: string;
  categories?: ReviewCategory[];
}

export interface LinkQuestionMapping {
  sourceQuestionId: string;
  targetQuestionId: string;
  sharedKey: string;
}

type MappingIdFactory = (sourceQuestionId: string, kind: 'auto' | 'manual' | 'add') => string;

const defaultMappingIdFactory: MappingIdFactory = (sourceQuestionId, kind) => {
  if (kind === 'manual') {
    return `mapping-${Date.now()}-new`;
  }

  if (kind === 'add') {
    return `mapping-${Date.now()}-${Math.random()}`;
  }

  return `mapping-${Date.now()}-${sourceQuestionId}`;
};

export const getAllItems = (category: ReviewCategory): ReviewItem[] => {
  const items = category.items || [];
  const subcategoryItems = category.subcategories
    ? category.subcategories.flatMap((subcategory) => getAllItems(subcategory))
    : [];

  return [...items, ...subcategoryItems];
};

export const getActiveQuestionFields = (item?: Pick<ReviewItem, 'questionsSchema'> | null): QuestionField[] => {
  return (item?.questionsSchema?.fields || []).filter((field) => !field.deprecated);
};

export const buildSharedKey = (sourceItemName: string, sourceQuestionId: string): string => {
  return `${sourceItemName.toLowerCase().replace(/\s+/g, '_')}_${sourceQuestionId}`;
};

export const getCompatibleQuestionTargets = (
  sourceField: QuestionField,
  targetFields: QuestionField[]
): QuestionField[] => {
  return targetFields.filter((targetField) => targetField.type === sourceField.type);
};

const buildQuestionMapping = (
  sourceItemName: string,
  sourceField: QuestionField,
  targetField: QuestionField,
  id: string
): QuestionMapping => ({
  id,
  sourceId: sourceField.id,
  targetId: targetField.id,
  sharedKey: buildSharedKey(sourceItemName, sourceField.id),
  sourceLabel: sourceField.label,
  targetLabel: targetField.label,
  sourceType: sourceField.type,
  targetType: targetField.type
});

export const buildInitialShareMappingState = ({
  sourceItemName,
  sourceFields,
  targetFields,
  isNewItem,
  createMappingId = defaultMappingIdFactory
}: {
  sourceItemName: string;
  sourceFields: QuestionField[];
  targetFields: QuestionField[];
  isNewItem: boolean;
  createMappingId?: MappingIdFactory;
}): { questionMappings: QuestionMapping[]; questionsToCopy: Set<string> } => {
  if (isNewItem) {
    return {
      questionMappings: [],
      questionsToCopy: new Set(sourceFields.map((field) => field.id))
    };
  }

  const questionMappings: QuestionMapping[] = [];
  const unmappableQuestions = new Set<string>();

  for (const sourceField of sourceFields) {
    let matchingTarget = targetFields.find((targetField) => targetField.id === sourceField.id);

    if (!matchingTarget) {
      matchingTarget = targetFields.find((targetField) =>
        targetField.label === sourceField.label && targetField.type === sourceField.type
      );
    }

    if (matchingTarget && sourceField.type === matchingTarget.type) {
      questionMappings.push(
        buildQuestionMapping(
          sourceItemName,
          sourceField,
          matchingTarget,
          createMappingId(sourceField.id, 'auto')
        )
      );
    } else if (getCompatibleQuestionTargets(sourceField, targetFields).length === 0) {
      unmappableQuestions.add(sourceField.id);
    }
  }

  const mappedSourceIds = new Set(questionMappings.map((mapping) => mapping.sourceId));
  const unmappedSources = sourceFields.filter((sourceField) =>
    !mappedSourceIds.has(sourceField.id) &&
    !unmappableQuestions.has(sourceField.id) &&
    getCompatibleQuestionTargets(sourceField, targetFields).length > 0
  );

  if (unmappedSources.length > 0) {
    const firstUnmapped = unmappedSources[0];
    const firstTarget = getCompatibleQuestionTargets(firstUnmapped, targetFields)[0];

    questionMappings.push(
      buildQuestionMapping(
        sourceItemName,
        firstUnmapped,
        firstTarget,
        createMappingId(firstUnmapped.id, 'manual')
      )
    );
  }

  return {
    questionMappings,
    questionsToCopy: unmappableQuestions
  };
};

export const updateQuestionMapping = ({
  mapping,
  mappingField,
  value,
  sourceItemName,
  sourceFields,
  targetFields
}: {
  mapping: QuestionMapping;
  mappingField: 'sourceId' | 'targetId';
  value: string;
  sourceItemName: string;
  sourceFields: QuestionField[];
  targetFields: QuestionField[];
}): QuestionMapping => {
  let sourceField: QuestionField | undefined;
  let targetField: QuestionField | undefined;

  if (mappingField === 'sourceId') {
    sourceField = sourceFields.find((field) => field.id === value);
    targetField = targetFields.find((field) => field.id === mapping.targetId);

    if (sourceField) {
      targetField = getCompatibleQuestionTargets(sourceField, targetFields)[0];
    }
  } else {
    sourceField = sourceFields.find((field) => field.id === mapping.sourceId);
    targetField = targetFields.find((field) => field.id === value);
  }

  if (!sourceField || !targetField) {
    return mapping;
  }

  return buildQuestionMapping(sourceItemName, sourceField, targetField, mapping.id);
};

export const buildNewQuestionMapping = ({
  sourceItemName,
  sourceFields,
  targetFields,
  existingMappings,
  questionsToCopy,
  createMappingId = defaultMappingIdFactory
}: {
  sourceItemName: string;
  sourceFields: QuestionField[];
  targetFields: QuestionField[];
  existingMappings: QuestionMapping[];
  questionsToCopy: Set<string>;
  createMappingId?: MappingIdFactory;
}): { mapping: QuestionMapping; error?: never } | { mapping: null; error: 'all-mapped' | 'no-compatible-targets' } => {
  const mappedSourceIds = new Set(existingMappings.map((mapping) => mapping.sourceId));
  const unmappedSources = sourceFields.filter((sourceField) =>
    !mappedSourceIds.has(sourceField.id) &&
    !questionsToCopy.has(sourceField.id) &&
    getCompatibleQuestionTargets(sourceField, targetFields).length > 0
  );

  if (unmappedSources.length === 0) {
    return { mapping: null, error: 'all-mapped' };
  }

  const firstUnmapped = unmappedSources[0];
  const firstTarget = getCompatibleQuestionTargets(firstUnmapped, targetFields)[0];

  if (!firstTarget) {
    return { mapping: null, error: 'no-compatible-targets' };
  }

  return {
    mapping: buildQuestionMapping(
      sourceItemName,
      firstUnmapped,
      firstTarget,
      createMappingId(firstUnmapped.id, 'add')
    )
  };
};

export const buildLinkQuestionMappings = (
  questionMappings: QuestionMapping[],
  questionsToCopy: Set<string>,
  sourceItemName: string
): LinkQuestionMapping[] => {
  const mappings: LinkQuestionMapping[] = questionMappings.map((mapping) => ({
    sourceQuestionId: mapping.sourceId,
    targetQuestionId: mapping.targetId,
    sharedKey: mapping.sharedKey
  }));

  for (const sourceQuestionId of questionsToCopy) {
    mappings.push({
      sourceQuestionId,
      targetQuestionId: sourceQuestionId,
      sharedKey: buildSharedKey(sourceItemName, sourceQuestionId)
    });
  }

  return mappings;
};
